import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Check } from "lucide-react";

import { breadcrumbJsonLd, seo } from "@/lib/seo";
import { Section } from "@/components/Section";
import { Container } from "@/components/Container";
import { Aurora } from "@/components/Aurora";
import { Eyebrow } from "@/components/Eyebrow";
import { Reveal } from "@/components/Reveal";
import { LeadForm } from "@/components/forms/LeadForm";
import { BOOKING_OPTIONS, type BookingOptionId } from "@/lib/booking";
import { cn } from "@/lib/utils";

const CalEmbed = React.lazy(() => import("@/components/booking/CalEmbed"));

const INTEREST_OPTIONS = [
  "AI Opportunity Assessment",
  "AI Implementation",
  "Workflow Automation",
  "AI Enablement & Training",
  "Business funding (SBA, grants, business plans)",
  "Not sure yet",
] as const;

const REASSURANCE = [
  { title: "No pitch deck.", body: "We ask about your operations, not your budget." },
  { title: "No obligation.", body: "Most first calls end with a suggestion, not a proposal." },
  {
    title: "A real consultant.",
    body: "The person on the call is the person who would do the work.",
  },
] as const;

export const Route = createFileRoute("/_site/book")({
  head: () =>
    seo({
      title: "Book a Free Small Business AI Consultation",
      description:
        "Book a free 20-minute AI consultation. We'll tell you where AI fits in your business, and where it doesn't — no cost, no obligation.",
      path: "/book",
      jsonLd: breadcrumbJsonLd([{ name: "Book a Call", path: "/book" }]),
    }),
  component: Page,
});

function Page() {
  const [selected, setSelected] = React.useState<BookingOptionId>("free-call");
  const option = BOOKING_OPTIONS.find((o) => o.id === selected) ?? BOOKING_OPTIONS[0]!;

  return (
    <Section tone="paper" className="relative overflow-hidden">
      <Aurora variant="light" className="opacity-70" />
      <Container className="relative">
        <div className="max-w-[60ch]">
          <Eyebrow>Book a call</Eyebrow>
          <h1 className="mt-4 font-display text-display-lg text-ink-900 text-balance">
            Book a free AI call.
          </h1>
          <p className="mt-5 text-body-lg text-ink-600">
            Free 20-minute call · No obligation · Slots most weekdays.
          </p>
        </div>

        <Reveal>
          <div
            role="radiogroup"
            aria-label="Choose a session"
            className="mt-10 grid gap-4 min-[900px]:grid-cols-2"
          >
            {BOOKING_OPTIONS.map((item) => {
              const active = item.id === selected;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setSelected(item.id)}
                  className={cn(
                    "rounded-2xl border p-6 text-left transition-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700",
                    active
                      ? "border-brand-600 bg-brand-100"
                      : "border-ink-200 bg-paper-0 hover:border-brand-600/40",
                  )}
                >
                  <span className="flex items-center justify-between gap-4">
                    <span className="font-display text-h3 text-ink-900">{item.name}</span>
                    <span
                      aria-hidden={!active}
                      className={cn(
                        "flex size-6 shrink-0 items-center justify-center rounded-full border",
                        active
                          ? "border-brand-600 bg-brand-600 text-paper-0"
                          : "border-ink-300 text-transparent",
                      )}
                    >
                      <Check size={14} strokeWidth={2} />
                      <span className="sr-only">{active ? "Selected" : ""}</span>
                    </span>
                  </span>
                  <span className="mt-3 flex items-baseline gap-3 font-mono text-body-sm uppercase tracking-[0.14em] text-ink-500">
                    <span>{item.duration}</span>
                    <span aria-hidden="true">·</span>
                    <span className="text-brand-700" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {item.price}
                    </span>
                  </span>
                  <span className="mt-4 block text-body text-ink-600">{item.description}</span>
                </button>
              );
            })}
          </div>
        </Reveal>

        <div className="mt-12">
          {option.calLink ? (
            <React.Suspense
              fallback={<p className="text-body text-ink-500">Loading the calendar…</p>}
            >
              <CalEmbed calLink={option.calLink} />
            </React.Suspense>
          ) : (
            <div className="max-w-[760px] rounded-2xl border border-ink-200 bg-paper-0 p-6 sm:p-8">
              <h2 className="font-display text-h3 text-ink-900">
                Tell us when you are free and we will confirm a slot.
              </h2>
              <LeadForm
                formType="booking_request"
                interestOptions={INTEREST_OPTIONS}
                submitLabel="Request a call"
                onSuccessRedirect="/thank-you?f=booking_request"
                showPreferredTimes
              />
            </div>
          )}
        </div>

        <Reveal>
          <ul className="mt-14 grid gap-8 border-t border-ink-200 pt-10 min-[900px]:grid-cols-3">
            {REASSURANCE.map((item) => (
              <li key={item.title}>
                <p className="text-body font-semibold text-ink-900">{item.title}</p>
                <p className="mt-2 text-body text-ink-600">{item.body}</p>
              </li>
            ))}
          </ul>
        </Reveal>
      </Container>
    </Section>
  );
}
